//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Dialog } from '@components/primitives/dialog/Dialog';
import { ContentDrawerTrigger } from '@content-drawer/ContentDrawerTrigger';
import { ContentDrawer } from '@content-drawer/filters/ContentDrawer';
import { useShouldHideContentDrawerRoutesConfig } from '@content-drawer/hooks/useShouldHideContentDrawerRoutesConfig';
import { getIsContentDrawerEnabled } from '@content-drawer/selectors/getContentDrawerSettings';
import { DialogContextProvider } from '@content-drawer/upload/DialogContextProvider';
import { DroppableImage } from '@content-drawer/upload/DroppableImage';
import { DroppableOverlay } from '@content-drawer/upload/DroppableOverlay';
import getAngularInjector from '@react-bridge/getAngularInjector';
import { contentDrawerActions } from '@redux/content-drawer/contentDrawerActions';
import { getIsSyndicationClient } from '@redux/selectors/getSessionInfo';
import { wait } from '@utils/wait';

const ANGULAR_ROOT_SCOPE = '$rootScope';
const OPEN_CONTENT_DRAWER_EVENT = 'universal-upload:open-content-drawer';
const CLOSE_CONTENT_DRAWER_EVENT = 'universal-upload:close-content-drawer';

export const ContentDrawerDialog = () => {
  const dispatch = useDispatch();
  const [isDragActive, setIsDragActive] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isRouteWithHiddenContentDrawerTrigger =
    useShouldHideContentDrawerRoutesConfig();

  const isContentDrawerEnabled = useSelector(getIsContentDrawerEnabled);
  const isSyndicationClient = useSelector(getIsSyndicationClient);
  const isTriggerVisible =
    !isSyndicationClient && !isRouteWithHiddenContentDrawerTrigger;

  useEffect(() => {
    const $rootScope = getAngularInjector().get(ANGULAR_ROOT_SCOPE);
    $rootScope.$on(OPEN_CONTENT_DRAWER_EVENT, () => {
      if (isContentDrawerEnabled) {
        setIsOpen(true);
      }
    });
    $rootScope.$on(CLOSE_CONTENT_DRAWER_EVENT, (_, forceClose) => {
      if (forceClose) {
        setIsOpen(false);
      }
    });
  }, [isContentDrawerEnabled]);

  const onDragChange = async (isDragActive: boolean) => {
    setIsDragActive(isDragActive);
    await wait(100); // Wait 100 milliseconds to avoid angular drag and drop events
    dispatch(
      contentDrawerActions.setOverrideSettings({
        isContentDrawerDroppableLayerActive: isDragActive,
      }),
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {isTriggerVisible && (
        <Dialog.Trigger asChild>
          <ContentDrawerTrigger onClick={() => setIsOpen((open) => !open)} />
        </Dialog.Trigger>
      )}
      <DialogContent
        ref={contentRef}
        onDragEnter={() => onDragChange(true)}
        onInteractOutside={(event: Event) => {
          event.preventDefault(); // Do not close the content drawer when clicking outside of the drawer.
        }}
      >
        <DialogContextProvider contentRef={contentRef}>
          <FlexContainer>
            <DroppableImage
              isActive={isDragActive}
              onDragEnter={() => onDragChange(true)}
            />
            <ContentDrawer onClose={() => setIsOpen(false)} />
            {isDragActive && (
              <DroppableOverlay onDragLeave={() => onDragChange(false)} />
            )}
          </FlexContainer>
        </DialogContextProvider>
      </DialogContent>
    </Dialog>
  );
};

const DialogContent = styled(Dialog.Content)(
  ({ theme }) => `
    display: flex;
    top: ${theme.spacings[0]};
    right: ${theme.spacings[0]};
    left: unset;
    height: 100vh;
    transform: unset;
  `,
);

const FlexContainer = styled.div`
  display: flex;
  width: 100vw;
`;
